/**
 * ReplyRulerDrawer — configuração da régua de resposta de um funil.
 *
 * Pilha vertical de blocos (decisão de UX do projeto workflow): gatilho no
 * topo (etapas monitoradas + canal), um bloco por degrau (tempo + ação +
 * conteúdo), e o degrau de encerramento pode mover o lead para outra etapa.
 * Cliente que responde zera a régua; humano atribuído cala a régua — essas
 * regras são do motor e estão explicadas na nota do rodapé.
 *
 * Esta tela cuida SÓ da régua transversal (inatividade): uma regra que vigia
 * várias etapas de um funil e age quando o cliente para de responder. A
 * sequência POR ETAPA (com vídeo/PDF e conteúdo próprio de cada etapa) mora na
 * engrenagem da etiqueta, em StageTrailSection — eram duas ideias diferentes
 * na mesma tela e o cliente tinha que entender "âncora" para escolher.
 *
 * Um funil pode ter mais de uma régua transversal; o seletor de chips no topo
 * escolhe qual está sendo editada. Backend: /api/reply-rulers.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  X, Timer, Plus, Trash2, Loader2, AlertCircle, Check, Paperclip,
} from 'lucide-react';
import replyRulerService from '../../services/global/replyRulerService';
import templateService from '../../services/global/templateService';
import authService from '../../services/global/authService';
import assignmentService from '../../services/global/assignmentService';
import fileUploadService from '../../services/global/fileUploadService';
import { positionalParamsInText } from '../../utils/templateVariables';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import TemplateMessageField from '../messages/TemplateMessageField';
import BusinessWindowFields from '../common/BusinessWindowFields';
import {
  analyzeTemplate, findTemplate, uniqueApproved, templateCapabilities,
  missingValues, valuesFromMessage,
} from '../../utils/templateParams';
import ChipMultiSelect from '../common/ChipMultiSelect';
import { channelOptions, supports, whereSupported } from '../../utils/channels';

// Opcoes de canal pelo mapa de capacidades (utils/channels.js), na ordem
// canonica (Business primeiro, como esta tela sempre mostrou).
const CHANNEL_OPTIONS = channelOptions('distinct');

const ACTION_OPTIONS = [
  { value: 'message', label: 'Enviar mensagem' },
  { value: 'template', label: 'Enviar template aprovado' },
  { value: 'agent', label: 'Deixar o agente decidir' },
];

// Degrau de arquivo só aparece com a trilha liberada (REPLY_RULER_TRAILS_ENABLED).
const MEDIA_ACTION = { value: 'media', label: 'Enviar arquivo' };

// Degraus de CRM e âncora por transferência: só com REPLY_RULER_CRM_ACTIONS_ENABLED
// (o backend diz via crm_actions_enabled). Mesmo seletor do fluxo de campanha.
const CRM_ACTIONS = [
  { value: 'transfer', label: 'Transferir para um responsável' },
  { value: 'roulette', label: 'Distribuir pela roleta (grupo)' },
];
const ANCHOR_OPTIONS = [
  { value: 'last_outbound', label: 'Última mensagem nossa sem resposta (padrão)' },
  { value: 'assignment', label: 'Transferência para um responsável (SLA do responsável)' },
];


const MAX_STEP_FILES = 3;

// after_minutes <-> (valor, unidade) para exibição amigável.
const toDisplay = (minutes) => {
  const m = Number(minutes) || 0;
  if (m >= 1440 && m % 1440 === 0) return { value: m / 1440, unit: 'days' };
  if (m >= 60 && m % 60 === 0) return { value: m / 60, unit: 'hours' };
  return { value: m, unit: 'minutes' };
};
const toMinutes = (value, unit) => {
  const v = Math.max(1, Number(value) || 1);
  if (unit === 'days') return v * 1440;
  if (unit === 'hours') return v * 60;
  return v;
};

const emptyStep = (afterMinutes) => ({
  after_minutes: afterMinutes,
  action: 'message',
  text: '',
  template_name: '',
  template_language: 'pt_BR',
  template_params: [],
  instruction: '',
  files: [],
  target_id: '',
  target_type: 'member',
  roulette_group_id: '',
  close: false,
  move_to_label_id: '',
});

const ReplyRulerDrawer = ({ isOpen, onClose, funnel, funnelLabels, colors, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);

  const [funnelRulers, setFunnelRulers] = useState([]);
  const [rulerId, setRulerId] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [name, setName] = useState('');
  const [channel, setChannel] = useState('wa_business');
  const [labelIds, setLabelIds] = useState([]);
  const [steps, setSteps] = useState([emptyStep(2), emptyStep(5)]);
  const [validateWithAgent, setValidateWithAgent] = useState(false);
  const [trailsEnabled, setTrailsEnabled] = useState(false);
  const [crmActionsEnabled, setCrmActionsEnabled] = useState(false);
  const [anchor, setAnchor] = useState('last_outbound');
  // Âncora por transferência: o relógio conta só a partir da PRIMEIRA vez que
  // o lead cai com alguém da equipe. Desligado, cada repasse reinicia.
  const [onlyFirstAssignment, setOnlyFirstAssignment] = useState(true);
  // Horário de silêncio: fora dele a régua segura o degrau em vez de mandar
  // mensagem de madrugada.
  const [quietEnabled, setQuietEnabled] = useState(false);
  const [quietStart, setQuietStart] = useState('20:00');
  const [quietEnd, setQuietEnd] = useState('08:00');
  // Janela comercial (flag automation_business_window): só no expediente do
  // dono e só nestes dias. Vive no mesmo quiet_hours.
  const [quietWindow, setQuietWindow] = useState({ business_hours_only: false, weekdays: [] });
  const [targets, setTargets] = useState([]);
  const [roulettes, setRoulettes] = useState([]);
  const [uploadingStep, setUploadingStep] = useState(null);

  // Horário de silêncio vive dentro de `guards`, junto com as guardas que a
  // tela não expõe (silêncio mínimo, intervalo, tentativas). Ler e gravar sem
  // apagar o resto.
  const applyQuiet = (guards) => {
    const q = (guards || {}).quiet_hours || {};
    setQuietEnabled(!!q.enabled);
    setQuietStart(q.start || '20:00');
    setQuietEnd(q.end || '08:00');
    setQuietWindow({
      business_hours_only: !!q.business_hours_only,
      weekdays: Array.isArray(q.weekdays) ? q.weekdays : [],
    });
  };
  // Régua aberta agora (para não perder guardas que a tela não mostra).
  const currentRuler = (funnelRulers || []).find((r) => r.id === rulerId) || null;


  const [allLabels, setAllLabels] = useState([]);
  const [allFunnels, setAllFunnels] = useState([]);
  const [templates, setTemplates] = useState([]);
  // Seletor único e editor de variáveis (2026-09-14). Sem a flag, como antes.
  const { flags } = useFeatureFlags();
  const templateUi = Boolean(flags.template_params_ui);
  const rulerCaps = templateCapabilities('ruler', flags);

  // Carrega régua existente do funil + catálogos de apoio.
  useEffect(() => {
    if (!isOpen || !funnel?.id) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const [rulersRes, labelsRes, funnelsRes] = await Promise.all([
          replyRulerService.list(),
          authService.getLabels(),
          authService.listFunnels(),
        ]);
        if (cancelled) return;
        const labels = labelsRes?.data || labelsRes?.labels || labelsRes || [];
        setAllLabels(Array.isArray(labels) ? labels : []);
        const funnels = funnelsRes?.data || funnelsRes || [];
        setAllFunnels(Array.isArray(funnels) ? funnels : []);
        setTrailsEnabled(!!rulersRes?.trails_enabled);
        setCrmActionsEnabled(!!rulersRes?.crm_actions_enabled);
        if (rulersRes?.crm_actions_enabled) {
          assignmentService.getTargets().then((d) => setTargets(d?.data || d || [])).catch(() => {});
          assignmentService.getTransferRoulettes().then((d) => setRoulettes(d?.data || [])).catch(() => {});
        }
        // Um funil pode ter VÁRIAS réguas: a trilha por etapa é uma por etapa
        // monitorada (Captação da myCar tem quatro). Sem esta lista o drawer
        // abria só a primeira e as outras ficavam invisíveis — sem como ligar.
        // Só as réguas TRANSVERSAIS (inatividade). A sequência por etapa
        // (anchor=stage_entry) é configurada na engrenagem da etiqueta — se
        // aparecesse aqui também, o cliente teria dois lugares para a mesma
        // coisa, que é justamente o que esta separação resolve.
        const doFunil = (rulersRes?.data || []).filter(
          (r) => r.pipeline_id === funnel.id && (r.anchor || 'last_outbound') !== 'stage_entry'
        );
        setFunnelRulers(doFunil);
        const existing = doFunil[0];
        if (existing) {
          setRulerId(existing.id);
          setName(existing.name || '');
          setEnabled(!!existing.enabled);
          setChannel(existing.channel || 'wa_business');
          setLabelIds(existing.label_ids || []);
          setSteps(
            (existing.steps || []).map((s) => ({
              ...emptyStep(s.after_minutes),
              ...s,
              files: s.files || [],
            }))
          );
          setValidateWithAgent(!!existing.validate_with_agent);
          setAnchor(existing.anchor || 'last_outbound');
          setOnlyFirstAssignment(existing.only_first_assignment !== false);
          applyQuiet(existing.guards);
        } else {
          setRulerId(null);
          setName('');
          setEnabled(false);
          setChannel('wa_business');
          setLabelIds([]);
          setSteps([emptyStep(2), emptyStep(5)]);
          setValidateWithAgent(false);
          setAnchor('last_outbound');
          setOnlyFirstAssignment(true);
          applyQuiet(null);
        }
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || 'Erro ao carregar a régua');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, funnel?.id]);

  // Templates aprovados (só quando o canal é Business e há degrau de template).
  const needsTemplates = channel === 'wa_business';
  useEffect(() => {
    if (!isOpen || !needsTemplates) return;
    let cancelled = false;
    templateService.list()
      .then((data) => {
        if (cancelled) return;
        const list = (data?.templates || []).filter(
          (t) => (t.status || '').toUpperCase() === 'APPROVED'
        );
        setTemplates(list.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      })
      .catch(() => { if (!cancelled) setTemplates([]); });
    return () => { cancelled = true; };
  }, [isOpen, needsTemplates]);

  const stageOptions = useMemo(() => {
    // Etapas de TODOS os funis para o destino do encerramento.
    const byFunnel = {};
    for (const l of allLabels) {
      const key = l.pipeline_id || 'default';
      (byFunnel[key] = byFunnel[key] || []).push(l);
    }
    return Object.entries(byFunnel).map(([pid, labels]) => ({
      funnelName: allFunnels.find((f) => f.id === pid)?.name || 'Funil',
      labels: labels.sort((a, b) => (a.order || 0) - (b.order || 0)),
    }));
  }, [allLabels, allFunnels]);

  const templateBody = (name) => {
    const t = templates.find((x) => x.name === name);
    return (t?.components || []).find(
      (c) => (c.type || '').toUpperCase() === 'BODY'
    )?.text || '';
  };

  // Carrega no formulário a régua escolhida no seletor (ou um rascunho novo).
  const loadRuler = (r) => {
    setError('');
    if (!r) {
      setRulerId(null);
      setName('');
      setEnabled(false);
      setChannel('wa_business');
      setLabelIds([]);
      setSteps([emptyStep(2), emptyStep(5)]);
      setValidateWithAgent(false);
      setAnchor('last_outbound');
      return;
    }
    setRulerId(r.id);
    setName(r.name || '');
    setEnabled(!!r.enabled);
    setChannel(r.channel || 'wa_business');
    setLabelIds(r.label_ids || []);
    setSteps((r.steps || []).map((s) => ({
      ...emptyStep(s.after_minutes), ...s, files: s.files || [],
    })));
    setValidateWithAgent(!!r.validate_with_agent);
    setAnchor(r.anchor || 'last_outbound');
    setOnlyFirstAssignment(r.only_first_assignment !== false);
    applyQuiet(r.guards);
  };

  const updateStep = (i, patch) => {
    setSteps((prev) => prev.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  };

  const clientErrors = useMemo(() => {
    const errs = [];
    if (!labelIds.length && anchor !== 'assignment') errs.push('Escolha ao menos uma etapa monitorada.');
    if (!steps.length) errs.push('Configure ao menos um degrau.');
    let last = 0;
    steps.forEach((s, i) => {
      if (s.after_minutes <= last) errs.push(`Degrau ${i + 1}: tempo deve crescer.`);
      last = s.after_minutes;
      if (s.action === 'message' && !s.text.trim()) errs.push(`Degrau ${i + 1}: escreva a mensagem.`);
      if (s.action === 'template' && !s.template_name) errs.push(`Degrau ${i + 1}: escolha o template.`);
      if (templateUi && s.action === 'template' && s.template_name) {
        const tpl = findTemplate(uniqueApproved(templates), s.template_name, s.template_language);
        const faltas = tpl ? missingValues(analyzeTemplate(tpl), valuesFromMessage(s), rulerCaps) : [];
        if (faltas.length) errs.push(`Degrau ${i + 1}: ${faltas[0]}`);
      }
      if (s.action === 'agent' && !s.instruction.trim()) errs.push(`Degrau ${i + 1}: escreva a instrução.`);
      if (s.action === 'media' && !(s.files || []).length) errs.push(`Degrau ${i + 1}: anexe o arquivo.`);
      if (s.action === 'transfer' && !s.target_id) errs.push(`Degrau ${i + 1}: escolha o responsável.`);
      if (s.action === 'roulette' && !s.roulette_group_id) errs.push(`Degrau ${i + 1}: escolha o grupo da roleta.`);
    });
    return errs;
  }, [labelIds, steps, anchor, templateUi, templates, rulerCaps]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: (name || '').trim() || `Régua: ${funnel?.name || 'funil'}`,
        enabled,
        channel,
        pipeline_id: funnel.id,
        label_ids: labelIds,
        anchor,
        only_first_assignment: anchor === 'assignment' ? onlyFirstAssignment : true,
        // Preserva as guardas que esta tela não expõe (silêncio mínimo,
        // intervalo entre degraus, tentativas) e só troca o que o cliente vê.
        guards: {
          ...(currentRuler?.guards || {}),
          quiet_hours: {
            enabled: quietEnabled, start: quietStart, end: quietEnd,
            business_hours_only: !!quietWindow.business_hours_only,
            weekdays: quietWindow.weekdays || [],
          },
        },
        validate_with_agent: validateWithAgent,
        steps: steps.map((s) => ({
          after_minutes: s.after_minutes,
          action: s.action,
          // No degrau de arquivo o texto é a legenda, então também vai.
          text: ['message', 'media'].includes(s.action) ? (s.text || '').trim() : null,
          template_name: s.action === 'template' ? s.template_name : null,
          template_language: s.action === 'template' ? (s.template_language || 'pt_BR') : null,
          template_params: s.action === 'template' && s.template_params?.length
            ? s.template_params.map((v) => (v || '').trim())
            : null,
          // Cabeçalho e botões só quando o envio da régua os manda (leva 2).
          ...(templateUi && s.action === 'template' && (rulerCaps.header || rulerCaps.media)
            ? { header_param: s.header_param || null } : {}),
          ...(templateUi && s.action === 'template' && rulerCaps.buttons
            ? { button_params: s.button_params || null } : {}),
          instruction: s.action === 'agent' ? s.instruction.trim() : null,
          target_id: s.action === 'transfer' ? (s.target_id || null) : null,
          target_type: s.action === 'transfer' ? (s.target_type || 'member') : null,
          roulette_group_id: s.action === 'roulette' ? (s.roulette_group_id || null) : null,
          files: s.action === 'media'
            ? (s.files || []).map((f) => ({
              url: f.url,
              type: f.type || 'document',
              filename: f.filename || 'arquivo',
              mimeType: f.mimeType || '',
            }))
            : null,
          close: !!s.close,
          move_to_label_id: s.close ? (s.move_to_label_id || null) : null,
        })),
      };
      const res = rulerId
        ? await replyRulerService.update(rulerId, payload)
        : await replyRulerService.create(payload);
      const salvo = res?.data;
      if (salvo?.id) {
        setRulerId(salvo.id);
        setFunnelRulers((prev) => {
          const i = prev.findIndex((r) => r.id === salvo.id);
          if (i === -1) return [...prev, salvo];
          const next = [...prev];
          next[i] = salvo;
          return next;
        });
      }
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
      onSaved?.(res?.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const inputStyle = {
    padding: '8px 10px', borderRadius: 8,
    border: `1px solid ${colors.borderColor}`,
    backgroundColor: colors.bgPrimary, color: colors.textPrimary,
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = {
    fontSize: 11, fontWeight: 600, color: colors.textSecondary,
    marginBottom: 4, display: 'block',
  };
  const blockStyle = {
    border: `1px solid ${colors.borderColor}`, borderRadius: 12,
    padding: 14, backgroundColor: colors.bgSecondary,
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex', justifyContent: 'flex-end',
      }}
      onClick={() => !saving && onClose?.()}
    >
      <div
        style={{
          width: 500, maxWidth: '100vw', height: '100%',
          backgroundColor: colors.bgPrimary,
          borderLeft: `1px solid ${colors.borderColor}`,
          display: 'flex', flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '16px 20px', borderBottom: `1px solid ${colors.borderColor}`,
        }}>
          <Timer size={18} color={colors.accentPrimary} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: colors.textPrimary }}>
              Régua de resposta
            </div>
            <div style={{ fontSize: 12, color: colors.textSecondary }}>
              {funnel?.name || 'Funil'}
            </div>
          </div>
          {/* Liga/desliga gritante */}
          <button
            onClick={() => setEnabled((v) => !v)}
            style={{
              padding: '6px 14px', borderRadius: 999, fontSize: 12, fontWeight: 700,
              border: `1px solid ${enabled ? '#22c55e' : colors.borderColor}`,
              backgroundColor: enabled ? '#22c55e22' : colors.bgTertiary,
              color: enabled ? '#22c55e' : colors.textSecondary, cursor: 'pointer',
            }}
          >
            {enabled ? 'Ligada' : 'Desligada'}
          </button>
          <button
            onClick={() => !saving && onClose?.()}
            style={{ background: 'none', border: 'none', color: colors.textSecondary, cursor: 'pointer' }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Corpo */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <Loader2 size={20} color={colors.accentPrimary} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : (
            <>
              {/* Seletor — só aparece quando o funil tem mais de uma régua
                  (caso da trilha por etapa: uma régua por etapa monitorada). */}
              {funnelRulers.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                  {funnelRulers.map((r) => {
                    const ativa = r.id === rulerId;
                    return (
                      <button
                        key={r.id}
                        onClick={() => loadRuler(r)}
                        title={r.enabled ? 'Ligada' : 'Desligada'}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          padding: '5px 12px', borderRadius: 999, fontSize: 12,
                          fontWeight: ativa ? 700 : 500,
                          border: `1px solid ${ativa ? colors.accentPrimary : colors.borderColor}`,
                          backgroundColor: ativa ? `${colors.accentPrimary}18` : 'transparent',
                          color: ativa ? colors.accentPrimary : colors.textSecondary,
                          cursor: 'pointer', maxWidth: 260,
                        }}
                      >
                        <span style={{
                          width: 7, height: 7, borderRadius: 999, flexShrink: 0,
                          backgroundColor: r.enabled ? '#22c55e' : colors.textTertiary,
                        }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.name || 'Régua'}
                        </span>
                      </button>
                    );
                  })}
                  <button
                    onClick={() => loadRuler(null)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '5px 12px', borderRadius: 999, fontSize: 12,
                      border: `1px dashed ${colors.borderColor}`,
                      backgroundColor: rulerId === null ? `${colors.accentPrimary}18` : 'transparent',
                      color: rulerId === null ? colors.accentPrimary : colors.textSecondary,
                      cursor: 'pointer',
                    }}
                  >
                    <Plus size={13} /> Nova
                  </button>
                </div>
              )}

              {/* Bloco gatilho */}
              <div style={blockStyle}>
                <div style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary, marginBottom: 10 }}>
                  Quando o cliente parar de responder
                </div>
                <label style={labelStyle}>Nome</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={`Régua: ${funnel?.name || 'funil'}`}
                  maxLength={120}
                  style={{ ...inputStyle, width: '100%', marginBottom: 10 }}
                />
                <label style={labelStyle}>Canal</label>
                <select
                  value={channel}
                  onChange={(e) => setChannel(e.target.value)}
                  style={{ ...inputStyle, width: '100%', marginBottom: 10 }}
                >
                  {CHANNEL_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
                {crmActionsEnabled && (
                  <>
                    <label style={labelStyle}>Início da contagem</label>
                    <select
                      value={anchor}
                      onChange={(e) => setAnchor(e.target.value)}
                      style={{ ...inputStyle, width: '100%', marginBottom: anchor === 'assignment' ? 6 : 10 }}
                    >
                      {ANCHOR_OPTIONS.map((a) => (
                        <option key={a.value} value={a.value}>{a.label}</option>
                      ))}
                    </select>
                    {anchor === 'assignment' && (
                      <>
                        <div style={{ fontSize: 11, color: colors.textTertiary, marginBottom: 8, lineHeight: 1.45 }}>
                          A contagem começa quando o lead é transferido para alguém da equipe e para
                          quando esse responsável responde. Etapas monitoradas são opcionais nesta âncora.
                        </div>
                        <label
                          style={{
                            display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10,
                            cursor: 'pointer', fontSize: 12, color: colors.textSecondary, lineHeight: 1.45,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={onlyFirstAssignment}
                            onChange={(e) => setOnlyFirstAssignment(e.target.checked)}
                            style={{ marginTop: 2, cursor: 'pointer' }}
                          />
                          <span>
                            Contar só no primeiro atendimento
                            <span style={{ display: 'block', color: colors.textTertiary, fontSize: 11 }}>
                              O relógio começa quando o lead cai com alguém da equipe pela primeira vez.
                              Repasse entre a equipe e a distribuição feita por esta régua não reiniciam a
                              contagem. Desmarque para que cada transferência abra um prazo novo.
                            </span>
                          </span>
                        </label>
                      </>
                    )}
                  </>
                )}
                <label style={labelStyle}>Etapas monitoradas deste funil</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {/* Até 8 etapas: chips diretos. Funil maior: seletor compacto
                      (só as monitoradas viram chip). */}
                  <div style={{ width: '100%' }}>
                    <ChipMultiSelect
                      options={funnelLabels || []}
                      value={labelIds}
                      onChange={setLabelIds}
                      colors={colors}
                      inlineThreshold={8}
                      addLabel="Adicionar etapa"
                      searchPlaceholder="Buscar etapa..."
                      emptyText="Este funil ainda não tem etapas."
                      noneText="Nenhuma etapa monitorada."
                    />
                  </div>
                  {trailsEnabled && (
                    <div style={{ width: '100%', marginTop: 12 }}>
                      <label style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        fontSize: 12, color: colors.textPrimary, cursor: 'pointer',
                      }}>
                        <input
                          type="checkbox"
                          checked={validateWithAgent}
                          onChange={(e) => setValidateWithAgent(e.target.checked)}
                        />
                        Conferir com a IA antes de cada envio
                      </label>
                      <div style={{ fontSize: 11, color: colors.textTertiary, marginTop: 4 }}>
                        O agente lê a conversa e segura a mensagem quando ela não faz
                        mais sentido: cliente que já comprou, já desistiu ou pediu para
                        não receber contato. Vale nos degraus de mensagem, arquivo e
                        template. Se a IA não responder, a mensagem é enviada.
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Degraus */}
              {steps.map((step, i) => {
                const disp = toDisplay(step.after_minutes);
                const tplParams = step.action === 'template' && step.template_name
                  ? positionalParamsInText(templateBody(step.template_name))
                  : [];
                return (
                  <div key={i} style={blockStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      <span style={{
                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                        backgroundColor: `${colors.accentPrimary}22`, color: colors.accentPrimary,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700,
                      }}>
                        {i + 1}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary, flex: 1 }}>
                        Degrau {i + 1}
                      </span>
                      {steps.length > 1 && (
                        <button
                          onClick={() => setSteps((prev) => prev.filter((_, j) => j !== i))}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                          title="Remover degrau"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                      <span style={{ fontSize: 12, color: colors.textSecondary }}>Após</span>
                      <input
                        type="number"
                        min={1}
                        value={disp.value}
                        onChange={(e) => updateStep(i, {
                          after_minutes: toMinutes(e.target.value, disp.unit),
                        })}
                        style={{ ...inputStyle, width: 70 }}
                      />
                      <select
                        value={disp.unit}
                        onChange={(e) => updateStep(i, {
                          after_minutes: toMinutes(disp.value, e.target.value),
                        })}
                        style={{ ...inputStyle }}
                      >
                        <option value="minutes">minutos</option>
                        <option value="hours">horas</option>
                        <option value="days">dias</option>
                      </select>
                      <span style={{ fontSize: 12, color: colors.textSecondary }}>sem resposta</span>
                    </div>

                    <select
                      value={step.action}
                      onChange={(e) => updateStep(i, { action: e.target.value })}
                      style={{ ...inputStyle, width: '100%', marginBottom: 8 }}
                    >
                      {[...ACTION_OPTIONS, ...(trailsEnabled ? [MEDIA_ACTION] : []), ...(crmActionsEnabled ? CRM_ACTIONS : [])]
                        .filter((a) => templateUi || a.value !== 'template' || supports(channel, 'template'))
                        .map((a) => {
                          // Item de tela compartilhada que o canal nao tem fica
                          // DESABILITADO com o motivo (mapa de capacidades).
                          const soBusiness = a.value === 'template' && !supports(channel, 'template');
                          return (
                            <option key={a.value} value={a.value} disabled={soBusiness}>
                              {a.label}{soBusiness ? ` (só no ${whereSupported('template')})` : ''}
                            </option>
                          );
                        })}
                    </select>

                    {step.action === 'message' && (
                      <>
                        <textarea
                          value={step.text}
                          onChange={(e) => updateStep(i, { text: e.target.value })}
                          placeholder={'Ex.: Oi {{primeiro_nome}}, conseguiu ver a mensagem acima?'}
                          maxLength={4096}
                          style={{ ...inputStyle, width: '100%', minHeight: 64, resize: 'vertical' }}
                        />
                        <div style={{ fontSize: 10, color: colors.textTertiary, marginTop: 4 }}>
                          Aceita variáveis como {'{{primeiro_nome}}'} e {'{{campo:Nome}}'}.
                        </div>
                      </>
                    )}
                    {step.action === 'template' && templateUi && (
                      <TemplateMessageField
                        templates={templates}
                        templateName={step.template_name || ''}
                        templateLanguage={step.template_language || ''}
                        onTemplateChange={(tpl) => updateStep(i, {
                          template_name: tpl?.name || '',
                          template_language: tpl?.language || 'pt_BR',
                        })}
                        values={valuesFromMessage(step)}
                        onValuesChange={(vals) => updateStep(i, {
                          template_params: vals.body_params,
                          ...(rulerCaps.header || rulerCaps.media ? { header_param: vals.header_param } : {}),
                          ...(rulerCaps.buttons ? { button_params: vals.button_params } : {}),
                        })}
                        capabilities={rulerCaps}
                        colors={colors}
                      />
                    )}
                    {step.action === 'template' && !templateUi && (
                      <>
                        <select
                          value={step.template_name}
                          onChange={(e) => {
                            const tpl = templates.find((t) => t.name === e.target.value);
                            updateStep(i, {
                              template_name: e.target.value,
                              template_language: tpl?.language || 'pt_BR',
                              template_params: [],
                            });
                          }}
                          style={{ ...inputStyle, width: '100%' }}
                        >
                          <option value="">
                            {templates.length === 0
                              ? 'Nenhum template aprovado'
                              : 'Selecione um template...'}
                          </option>
                          {templates.map((t) => (
                            <option key={t.name} value={t.name}>{t.name}</option>
                          ))}
                        </select>
                        {tplParams.map((n, pi) => (
                          <input
                            key={n}
                            value={step.template_params?.[pi] || ''}
                            onChange={(e) => {
                              const next = [...(step.template_params || [])];
                              next[pi] = e.target.value;
                              updateStep(i, { template_params: next });
                            }}
                            placeholder={`Valor do {{${n}}} — ex.: ${n === 1 ? '{{primeiro_nome}}' : 'texto ou {{campo:Nome}}'}`}
                            style={{ ...inputStyle, width: '100%', marginTop: 6 }}
                          />
                        ))}
                      </>
                    )}
                    {step.action === 'media' && (
                      <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {(step.files || []).map((f, fi) => (
                            <div
                              key={`${f.url}-${fi}`}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '6px 10px', borderRadius: 6,
                                border: `1px solid ${colors.borderColor}`,
                                backgroundColor: colors.bgTertiary,
                              }}
                            >
                              <Paperclip size={13} color={colors.textSecondary} />
                              <span style={{
                                flex: 1, fontSize: 12, color: colors.textPrimary,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}>
                                {f.filename || 'arquivo'}
                              </span>
                              <button
                                onClick={() => updateStep(i, {
                                  files: (step.files || []).filter((_, x) => x !== fi),
                                })}
                                style={{ background: 'none', border: 'none', color: colors.textTertiary, cursor: 'pointer' }}
                                title="Remover arquivo"
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          ))}
                        </div>
                        {(step.files || []).length < MAX_STEP_FILES && (
                          <label
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              marginTop: (step.files || []).length ? 8 : 0,
                              padding: '6px 12px', borderRadius: 6, fontSize: 12,
                              border: `1px dashed ${colors.borderColor}`,
                              color: colors.textSecondary,
                              cursor: uploadingStep === i ? 'wait' : 'pointer',
                            }}
                          >
                            {uploadingStep === i
                              ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
                              : <Paperclip size={13} />}
                            {uploadingStep === i ? 'Enviando...' : 'Anexar arquivo'}
                            <input
                              type="file"
                              accept={fileUploadService.getAcceptString()}
                              disabled={uploadingStep !== null}
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                e.target.value = '';
                                if (!file) return;
                                setUploadingStep(i);
                                setError('');
                                try {
                                  const up = await fileUploadService.uploadFile(file, null, { channel });
                                  setSteps((prev) => prev.map((s, j) => (j === i
                                    ? { ...s, files: [...(s.files || []), up] }
                                    : s)));
                                } catch (err) {
                                  setError(err?.message || 'Erro ao anexar o arquivo');
                                } finally {
                                  setUploadingStep(null);
                                }
                              }}
                              style={{ display: 'none' }}
                            />
                          </label>
                        )}
                        <input
                          value={step.text || ''}
                          onChange={(e) => updateStep(i, { text: e.target.value })}
                          placeholder="Legenda do arquivo (opcional)"
                          maxLength={1024}
                          style={{ ...inputStyle, width: '100%', marginTop: 8 }}
                        />
                      </>
                    )}
                    {step.action === 'agent' && (
                      <>
                        <textarea
                          value={step.instruction}
                          onChange={(e) => updateStep(i, { instruction: e.target.value })}
                          placeholder="Ex.: Retome a conversa com leveza; se o cliente demonstrou despedida ou irritação, não insista."
                          maxLength={2000}
                          style={{ ...inputStyle, width: '100%', minHeight: 64, resize: 'vertical' }}
                        />
                        <div style={{ fontSize: 10, color: colors.textTertiary, marginTop: 4 }}>
                          O agente lê a conversa e pode decidir não enviar.
                        </div>
                      </>
                    )}

                    {step.action === 'transfer' && (
                      <select
                        value={step.target_id || ''}
                        onChange={(e) => {
                          const alvo = targets.find((t) => t.id === e.target.value);
                          updateStep(i, { target_id: e.target.value, target_type: alvo?.type || 'member' });
                        }}
                        style={{ ...inputStyle, width: '100%' }}
                      >
                        <option value="">Escolha o responsável...</option>
                        {targets.map((t) => (
                          <option key={t.id} value={t.id}>{t.name} ({t.type === 'agent' ? 'IA' : 'equipe'})</option>
                        ))}
                      </select>
                    )}
                    {step.action === 'roulette' && (
                      <select
                        value={step.roulette_group_id || ''}
                        onChange={(e) => updateStep(i, { roulette_group_id: e.target.value })}
                        style={{ ...inputStyle, width: '100%' }}
                      >
                        <option value="">{roulettes.length ? 'Escolha o grupo...' : 'Nenhum grupo de roleta criado'}</option>
                        {roulettes.map((g) => (
                          <option key={g.id} value={g.id}>{g.name}</option>
                        ))}
                      </select>
                    )}
                    {['transfer', 'roulette'].includes(step.action) && (
                      <div style={{ fontSize: 10, color: colors.textTertiary, marginTop: 4 }}>
                        Este degrau não envia mensagem: transfere o contato. A régua continua nos
                        degraus seguintes mesmo com o lead nas mãos do responsável que ela escolheu.
                      </div>
                    )}

                    {/* Encerramento */}
                    <label style={{
                      display: 'flex', alignItems: 'center', gap: 8, marginTop: 10,
                      fontSize: 12, color: colors.textPrimary, cursor: 'pointer',
                    }}>
                      <input
                        type="checkbox"
                        checked={!!step.close}
                        onChange={(e) => updateStep(i, { close: e.target.checked })}
                      />
                      Encerrar a régua neste degrau
                    </label>
                    {step.close && (
                      <div style={{ marginTop: 8 }}>
                        <label style={labelStyle}>Mover o lead para a etapa (opcional)</label>
                        <select
                          value={step.move_to_label_id || ''}
                          onChange={(e) => updateStep(i, { move_to_label_id: e.target.value })}
                          style={{ ...inputStyle, width: '100%' }}
                        >
                          <option value="">Não mover</option>
                          {stageOptions.map((g) => (
                            <optgroup key={g.funnelName} label={g.funnelName}>
                              {g.labels.map((l) => (
                                <option key={l.id} value={l.id}>{l.name}</option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                );
              })}

              <button
                onClick={() => setSteps((prev) => [
                  ...prev,
                  emptyStep((prev[prev.length - 1]?.after_minutes || 5) * 2),
                ])}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '10px 12px', borderRadius: 10,
                  border: `1px dashed ${colors.borderColor}`, background: 'transparent',
                  color: colors.textSecondary, fontSize: 13, cursor: 'pointer',
                }}
              >
                <Plus size={14} /> Adicionar degrau
              </button>

              {/* Horário de silêncio: um degrau vencido de madrugada espera o
                  expediente em vez de acordar o cliente. */}
              <div style={{
                border: `1px solid ${colors.borderColor}`, borderRadius: 10, padding: 12,
              }}>
                <label
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                    fontSize: 13, color: colors.textPrimary, fontWeight: 600,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={quietEnabled}
                    onChange={(e) => setQuietEnabled(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  Não enviar de madrugada
                </label>
                <div style={{ fontSize: 11, color: colors.textTertiary, marginTop: 6, lineHeight: 1.45 }}>
                  Degrau que vencer dentro da faixa espera o horário de volta. Nada é perdido,
                  só adiado.
                </div>
                {quietEnabled && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                    <span style={{ fontSize: 12, color: colors.textSecondary }}>Das</span>
                    <input
                      type="time"
                      value={quietStart}
                      onChange={(e) => setQuietStart(e.target.value)}
                      style={{ ...inputStyle, width: 110 }}
                    />
                    <span style={{ fontSize: 12, color: colors.textSecondary }}>às</span>
                    <input
                      type="time"
                      value={quietEnd}
                      onChange={(e) => setQuietEnd(e.target.value)}
                      style={{ ...inputStyle, width: 110 }}
                    />
                  </div>
                )}
                {flags.automation_business_window && (
                  <BusinessWindowFields
                    colors={colors}
                    value={quietWindow}
                    onChange={setQuietWindow}
                  />
                )}
              </div>

              <div style={{ fontSize: 11, color: colors.textTertiary, lineHeight: 1.5 }}>
                Os tempos contam a partir da última mensagem sua (ou do agente).
                Se o cliente responder, a régua zera na hora. Contato atribuído a
                um membro humano não recebe a régua, e contato que sair das
                etapas monitoradas é cancelado.
                <br /><br />
                Para uma sequência que começa quando o contato ENTRA numa etapa
                (com vídeo, PDF e conteúdo próprio de cada etapa), use a
                engrenagem da etiqueta no quadro.
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 20px', borderTop: `1px solid ${colors.borderColor}`,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          {error && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 12, color: '#ef4444',
            }}>
              <AlertCircle size={13} /> {error}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => onClose?.()}
              disabled={saving}
              style={{
                flex: 1, padding: '10px 12px', borderRadius: 10,
                border: `1px solid ${colors.borderColor}`, background: 'transparent',
                color: colors.textSecondary, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Fechar
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading || clientErrors.length > 0}
              title={clientErrors.length ? clientErrors.join(' ') : undefined}
              style={{
                flex: 2, padding: '10px 12px', borderRadius: 10, border: 'none',
                backgroundColor: clientErrors.length ? colors.bgTertiary : colors.accentPrimary,
                color: clientErrors.length ? colors.textTertiary : '#fff',
                fontSize: 13, fontWeight: 600,
                cursor: saving || clientErrors.length ? 'not-allowed' : 'pointer',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}
            >
              {saving ? (
                <>
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                  Salvando…
                </>
              ) : savedFlash ? (
                <>
                  <Check size={14} /> Salvo
                </>
              ) : (
                'Salvar régua'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReplyRulerDrawer;
